import { Modal } from "antd";
import { useEffect } from "react";
import { Flex, Text, Skeleton } from "@chakra-ui/react";
import OutlineButton from "components/Button/OutlineButton";
import Field from "components/Input/Field";
import PrimaryButton from "components/Button/PrimaryButton";
import CardProvider from "./components/CardProvider";
import CardRegrasVencimento from "./components/CardRegrasVencimento";
import { useLoading } from "components/GlobalLoading";
import { BiBot } from "react-icons/bi";
import useTiposNotificacoes from "features/robo-cobranca/hooks/useTiposNotificacoes";
import useNotificacaoProviders from "features/robo-cobranca/hooks/useNotificacaoProviders";
import useNotificacaoCobranca from "features/robo-cobranca/hooks/useNotificacaoCobranca";
import useSaveNotificacaoCobranca from "features/robo-cobranca/hooks/useSaveNotificacaoCobranca";
import { NotificacaoConfiguracaoForm } from "features/robo-cobranca/typings";
import { showErrorAxios, showSuccess } from "infra/helpers/alerta";
import { useForm } from "react-hook-form";

function ModalNotificacao({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { handleSubmit, control, watch, reset } =
    useForm<NotificacaoConfiguracaoForm>({
      defaultValues: {
        providers: [],
        tipos: [],
      },
    });

  const { data: tipos, isFetching: isLoadingTipos } = useTiposNotificacoes();
  const { data: providers, isFetching: isLoadingProviders } =
    useNotificacaoProviders();

  const { data: notificacaoCobranca, isFetching: isLoading } =
    useNotificacaoCobranca();

  const { mutate, isLoading: isLoadingSave } = useSaveNotificacaoCobranca();

  const handleSave = (values: NotificacaoConfiguracaoForm) => {
    values.providers.forEach((tipo, index) => {
      tipo.notificacaoProviderId = providers![index].id;
      tipo.ativo = !!tipo.ativo;
    });

    values.tipos.forEach((tipo, index) => {
      tipo.tipoNotificacaoCobrancaId = tipos![index].id;
      tipo.items = tipo.items ?? [];
    });

    mutate(values, {
      onError: (err) => {
        showErrorAxios(err);
      },
      onSuccess: async () => {
        await showSuccess();
        onClose();
      },
    });
  };

  useEffect(() => {
    if (notificacaoCobranca && tipos && providers) {
      const next = { ...notificacaoCobranca };

      next.providers = providers.map((p) => {
        const exists = notificacaoCobranca.providers.find(
          (p2) => p2.notificacaoProviderId === p.id
        );

        return {
          ativo: exists ? exists.ativo : false,
          notificacaoProviderId: p.id,
        };
      });

      next.tipos = tipos.map((t) => {
        const exists = notificacaoCobranca.tipos.find(
          (t2) => t2.tipoNotificacaoCobrancaId === t.id
        );

        return {
          items: exists ? exists.items : [],
          tipoNotificacaoCobrancaId: t.id,
          templateEmail: exists ? exists.templateEmail : undefined,
        };
      });

      reset(next);
    }
  }, [reset, notificacaoCobranca, tipos, providers]);

  useLoading(isLoading);

  const formTipos = watch("tipos");

  return (
    <Modal
      onCancel={onClose}
      visible={isOpen}
      width="1000px"
      footer={
        <Flex justify="end">
          <OutlineButton isDisabled={isLoadingSave} onClick={onClose} mr={4}>
            Cancelar
          </OutlineButton>
          <PrimaryButton
            isDisabled={isLoadingSave}
            isLoading={isLoadingSave}
            onClick={handleSubmit(handleSave)}
          >
            Salvar
          </PrimaryButton>
        </Flex>
      }
    >
      <Flex gap="4px" alignItems={"center"}>
        <BiBot fontSize={20} />
        <Text fontSize="18px" fontWeight="500">
          Robô de Cobrança
        </Text>
      </Flex>
      <Flex mt="37px">
        <Text fontSize="16px" fontWeight="500">
          Formas de notificar
        </Text>
      </Flex>
      <Flex mt="18px" gap="30px">
        {!isLoadingProviders &&
          providers?.map((item, index) => (
            <Field
              control={control}
              inputProps={{
                defaultValue: false,
              }}
              name={`providers.${index}.ativo`}
              render={({ field }) => (
                <CardProvider
                  id={item.id}
                  key={index}
                  nome={item.nome}
                  valor={item.valor}
                  imagemUrl={item.imagemUrl}
                  onChange={(v) => {
                    field.onChange(v);
                  }}
                  value={field.value}
                />
              )}
            />
          ))}

        {isLoadingProviders ? (
          <Flex flex={1} gap="32px">
            <Skeleton h="120px" rounded="8px" flex={1} />
            <Skeleton h="120px" rounded="8px" flex={1} />
            <Skeleton h="120px" rounded="8px" flex={1} />
          </Flex>
        ) : null}
      </Flex>
      <Flex mt="60px">
        <Text fontSize="16px" fontWeight="500">
          Regra de Notificação
        </Text>
      </Flex>
      <Flex mt="21px" gap="32px">
        {!isLoadingTipos &&
          tipos?.map((tipo, index) => (
            <CardRegrasVencimento
              isInvalid={
                !formTipos[index]?.templateEmail &&
                formTipos[index]?.items?.length > 0
              }
              control={control}
              parentIndex={index}
              watch={watch}
              titulo={tipo.nome}
              tituloCard={`${index !== 1 ? "Dias" : ""} ${tipo.nome}`}
              renderAddButton={index !== 1}
              tipoNotificacaoId={tipo.id}
            />
          ))}

        {isLoadingTipos ? (
          <Flex flex={1} gap="32px">
            <Skeleton h="350px" rounded="8px" flex={1} />
            <Skeleton h="350px" rounded="8px" flex={1} />
            <Skeleton h="350px" rounded="8px" flex={1} />
          </Flex>
        ) : null}
      </Flex>
    </Modal>
  );
}

export default ModalNotificacao;
